import { useApp } from "@/context/AppContext";
import { useUI } from "@/context/UIContext";
import { Icon } from "@/lib/icons";
import { CheckCircle2, Circle, Plus, Flag } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { strToDate } from "@/lib/timeUtils";

const COLUMNS = [
  { key: "high", label: "High Priority", color: "#EF4444" },
  { key: "medium", label: "Medium", color: "#F59E0B" },
  { key: "low", label: "Low", color: "#10B981" },
];

export function TasksPage() {
  const { tasks, tasksApi, getCategory } = useApp();
  const { openTaskModal } = useUI();
  const toggle = (t) => tasksApi.update(t.id, { completed: !t.completed });

  return (
    <div className="h-full overflow-y-auto p-4 lg:p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-display text-2xl font-extrabold" style={{ color: "var(--text-primary)" }}>Tasks & Focus</h2>
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>{tasks.filter((t) => t.completed).length} of {tasks.length} completed</p>
        </div>
        <button onClick={() => openTaskModal({})} className="h-10 px-4 rounded-xl flex items-center gap-2 text-sm font-semibold text-white" style={{ background: "var(--accent-primary)" }} data-testid="button-new-task">
          <Plus size={18} /> New Task
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {COLUMNS.map((col) => {
          const colTasks = tasks.filter((t) => t.priority === col.key);
          return (
            <div key={col.key} className="rounded-3xl p-4" style={{ background: "var(--bg-timeline-track)", border: "1px solid var(--border-subtle)" }} data-testid={`task-column-${col.key}`}>
              <div className="flex items-center gap-2 mb-3">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: col.color }} />
                <h3 className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>{col.label}</h3>
                <span className="ml-auto text-xs font-mono px-2 py-0.5 rounded-md" style={{ background: "var(--bg-card)", color: "var(--text-muted)" }}>{colTasks.length}</span>
              </div>
              <div className="space-y-2">
                <AnimatePresence>
                  {colTasks.map((t) => {
                    const c = t.color || getCategory(t.category_id)?.color || col.color;
                    return (
                      <motion.div key={t.id} layout initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}
                        className="group flex items-start gap-2.5 p-3 rounded-2xl cursor-pointer" style={{ background: "var(--bg-card)", border: "1px solid var(--border-subtle)", borderLeft: `3px solid ${c}` }}
                        onClick={() => openTaskModal(t)} data-testid="task-item">
                        <button onClick={(e) => { e.stopPropagation(); toggle(t); }} className="mt-0.5 shrink-0" data-testid="task-item-checkbox">
                          {t.completed ? <CheckCircle2 size={20} className="check-pop" style={{ color: c }} /> : <Circle size={20} style={{ color: "var(--text-muted)" }} />}
                        </button>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium" style={{ color: t.completed ? "var(--text-muted)" : "var(--text-primary)", textDecoration: t.completed ? "line-through" : "none" }}>{t.title}</p>
                          <div className="flex items-center gap-2 mt-1">
                            {t.category_id && <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: `color-mix(in srgb, ${c} 22%, transparent)`, color: "var(--text-secondary)" }}>{getCategory(t.category_id)?.name}</span>}
                            {t.due_date && <span className="text-[10px] font-mono" style={{ color: "var(--text-muted)" }}>{strToDate(t.due_date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</span>}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
                {colTasks.length === 0 && <p className="text-xs py-3 text-center" style={{ color: "var(--text-muted)" }}>No tasks</p>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
